import { useEffect, useMemo, type ComponentType, type ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  ArrowSquareOut,
  CaretRight,
  CheckCircle,
  DotsThreeOutline,
  ForkKnife,
  GlobeHemisphereWest,
  HandHeart,
  House,
  MagnifyingGlass,
  MapPin,
  MapTrifold,
  PhoneCall,
  ShieldCheck,
  UserCircle,
  UsersThree,
} from '@phosphor-icons/react';
import { LoginButton } from '@/components/LoginButton';
import { NotificationBell } from '@/components/NotificationBell';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { useAuth } from '@/contexts/AuthContext';
import { US_STATES } from '@/lib/data';
import { LOCALES, useI18n, type Locale } from '@/lib/i18n';
import { cn } from '@/lib/utils';

interface LayoutProps {
  children: ReactNode;
}

type Icon = ComponentType<{
  size?: number | string;
  weight?: 'regular' | 'bold' | 'fill' | 'duotone';
  className?: string;
}>;

interface NavigationItem {
  label: string;
  href: string;
  icon: Icon;
  isActive: (pathname: string) => boolean;
}

function titleFromSlug(value: string): string {
  return decodeURIComponent(value)
    .replace(/-\d{5}(?:-\d{4})?$/u, '')
    .replace(/[-_]+/gu, ' ')
    .replace(/\b\w/gu, (letter) => letter.toUpperCase())
    .replace(/\bMc([a-z])/gu, (_, letter: string) => `Mc${letter.toUpperCase()}`);
}

function getRouteLabel(pathname: string): string {
  if (pathname === '/') return 'National help network';
  if (pathname === '/food') return 'Food help';
  if (pathname.includes('/community')) return 'Community portal';
  if (pathname === '/connecting-hands') return 'Connecting hands';
  if (pathname === '/account') return 'Account';
  if (pathname === '/dashboard') return 'Dashboard';
  if (pathname === '/about') return 'About';
  if (pathname === '/contact') return 'Contact';
  if (pathname === '/county-verification') return 'Coverage';

  const segments = pathname.split('/').filter(Boolean);
  const stateRecord = US_STATES.find((item) => item.code.toLowerCase() === segments[0]?.toLowerCase());
  if (!stateRecord) return 'Planet Earth Help';
  if (segments.length >= 3) return `${titleFromSlug(segments[2])} Town Center`;
  if (segments.length === 2) return titleFromSlug(segments[1]);
  return `${stateRecord.name} network`;
}

function getGeographyContext(pathname: string) {
  const segments = pathname.split('/').filter(Boolean);
  const stateRecord = US_STATES.find((item) => item.code.toLowerCase() === segments[0]?.toLowerCase());
  if (!stateRecord) return null;

  const stateCode = stateRecord.code;
  const countySlug = segments[1] && !['community', 'support'].includes(segments[1]) ? segments[1] : undefined;
  const citySlug = segments[2] && !['community', 'support'].includes(segments[2]) ? segments[2] : undefined;

  return {
    stateCode,
    stateName: stateRecord.name,
    stateHref: `/${stateCode}`,
    countyName: countySlug ? titleFromSlug(countySlug) : undefined,
    countyHref: countySlug ? `/${stateCode}/${countySlug}` : undefined,
    cityName: citySlug ? titleFromSlug(citySlug) : undefined,
    cityHref: citySlug ? `/${stateCode}/${countySlug}/${citySlug}` : undefined,
  };
}

function RailLink({ item, pathname }: { item: NavigationItem; pathname: string }) {
  const active = item.isActive(pathname);
  const IconComponent = item.icon;

  return (
    <Link
      to={item.href}
      aria-current={active ? 'page' : undefined}
      className={cn(
        'group flex min-h-11 items-center gap-3 rounded-2xl px-3 text-sm font-bold transition-colors',
        active
          ? 'bg-[#173f31] text-[#fff8e7] shadow-[0_12px_26px_rgba(23,63,49,0.18)]'
          : 'text-[#425048] hover:bg-[#173f31]/7 hover:text-[#173f31]',
      )}
    >
      <span
        className={cn(
          'grid h-8 w-8 shrink-0 place-items-center rounded-xl transition-colors',
          active ? 'bg-white/12 text-[#f4c88c]' : 'bg-[#173f31]/7 text-[#436b59] group-hover:bg-[#173f31]/10',
        )}
      >
        <IconComponent size={18} weight={active ? 'fill' : 'duotone'} />
      </span>
      <span>{item.label}</span>
    </Link>
  );
}

function MobileTab({ item, pathname }: { item: NavigationItem; pathname: string }) {
  const active = item.isActive(pathname);
  const IconComponent = item.icon;

  return (
    <Link
      to={item.href}
      aria-current={active ? 'page' : undefined}
      className={cn(
        'relative flex min-h-[62px] min-w-0 flex-1 flex-col items-center justify-center gap-1 rounded-2xl px-1 text-[10px] font-black tracking-[-0.01em] transition-colors',
        active ? 'text-[#173f31]' : 'text-[#657168] hover:bg-[#173f31]/6 hover:text-[#173f31]',
      )}
    >
      {active && <span className="absolute top-1 h-1 w-7 rounded-full bg-[#b26b45]" aria-hidden="true" />}
      <IconComponent size={22} weight={active ? 'fill' : 'duotone'} />
      <span className="truncate">{item.label}</span>
    </Link>
  );
}

export const Layout = ({ children }: LayoutProps) => {
  const location = useLocation();
  const { userProfile } = useAuth();
  const { locale, setLocale, t } = useI18n();
  const geography = useMemo(() => getGeographyContext(location.pathname), [location.pathname]);
  const routeLabel = useMemo(() => getRouteLabel(location.pathname), [location.pathname]);

  const navigation = useMemo<NavigationItem[]>(
    () => [
      { label: t('nav.home'), href: '/', icon: House, isActive: (path) => path === '/' },
      { label: t('nav.food'), href: '/food', icon: ForkKnife, isActive: (path) => path.startsWith('/food') },
      {
        label: t('shell.townCenters'),
        href: '/#state-directory',
        icon: MapTrifold,
        isActive: (path) => Boolean(getGeographyContext(path)),
      },
      {
        label: t('home.community'),
        href: geography?.countyHref ? `${geography.countyHref}/community` : '/community',
        icon: UsersThree,
        isActive: (path) => path.includes('/community'),
      },
      {
        label: t('home.howToHelp'),
        href: '/connecting-hands',
        icon: HandHeart,
        isActive: (path) => path.startsWith('/connecting-hands'),
      },
    ],
    [geography?.countyHref, t],
  );

  const mobileNavigation = navigation.slice(0, 4);

  useEffect(() => {
    if (location.hash) {
      window.requestAnimationFrame(() => document.querySelector(location.hash)?.scrollIntoView({ block: 'start' }));
      return;
    }
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [location.hash, location.pathname]);

  return (
    <div className="peh-workspace min-h-dvh bg-[#eee9dc] font-sans text-[#17231d] antialiased">
      <div
        className="pointer-events-none fixed inset-0 opacity-55"
        aria-hidden="true"
        style={{
          backgroundImage:
            "linear-gradient(180deg, rgba(246,242,232,0.78), rgba(237,233,221,0.92)), url('/textures/civic-atlas-v1.webp')",
          backgroundPosition: 'center top',
          backgroundSize: 'cover, 1152px auto',
        }}
      />

      <div className="relative mx-auto grid min-h-dvh max-w-[1920px] lg:grid-cols-[248px_minmax(0,1fr)] 2xl:grid-cols-[248px_minmax(0,1fr)_304px]">
        <aside
          aria-label="Application navigation"
          className="sticky top-0 z-30 hidden h-dvh overflow-y-auto border-r border-[#173f31]/10 bg-[#faf6ec]/88 p-4 backdrop-blur-2xl lg:flex lg:flex-col"
        >
          <Link
            to="/"
            className="group mb-7 flex items-center gap-3 rounded-2xl p-2 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[#b26b45]/35"
          >
            <span className="grid h-12 w-12 shrink-0 place-items-center rounded-[1.1rem] bg-[#173f31] text-[#fff8e7] shadow-[0_14px_30px_rgba(23,63,49,0.22)] transition-transform group-hover:-rotate-2">
              <HandHeart size={25} weight="fill" />
            </span>
            <span className="min-w-0">
              <span className="block font-serif text-xl font-black leading-[0.95] tracking-[-0.045em] text-[#17231d]">
                Planet Earth
                <br />
                Help
              </span>
            </span>
          </Link>

          <div className="mb-3 px-3 text-[10px] font-black uppercase tracking-[0.22em] text-[#7a817a]">
            {t('shell.publicNetwork')}
          </div>
          <nav aria-label={t('nav.primaryNavigation')} className="space-y-1">
            {navigation.map((item) => (
              <RailLink key={item.href} item={item} pathname={location.pathname} />
            ))}
          </nav>

          {geography && (
            <section className="mt-7 rounded-[1.4rem] border border-[#173f31]/10 bg-white/58 p-3 shadow-[0_14px_34px_rgba(49,57,49,0.07)]">
              <div className="mb-3 flex items-center gap-2 px-1 text-[10px] font-black uppercase tracking-[0.2em] text-[#7a817a]">
                <MapPin size={14} weight="fill" className="text-[#b26b45]" />
                {t('shell.yourPlace')}
              </div>
              <div className="space-y-1">
                <Link
                  to={geography.stateHref}
                  className="flex min-h-10 items-center justify-between rounded-xl px-2 text-sm font-bold text-[#26362e] hover:bg-[#173f31]/7"
                >
                  <span className="truncate">{geography.stateName}</span>
                  <CaretRight size={14} />
                </Link>
                {geography.countyHref && geography.countyName && (
                  <Link
                    to={geography.countyHref}
                    className="flex min-h-10 items-center justify-between rounded-xl px-2 text-sm font-bold text-[#26362e] hover:bg-[#173f31]/7"
                  >
                    <span className="truncate">{geography.countyName}</span>
                    <CaretRight size={14} />
                  </Link>
                )}
                {geography.cityHref && geography.cityName && (
                  <Link
                    to={geography.cityHref}
                    className="flex min-h-10 items-center justify-between rounded-xl bg-[#b26b45]/10 px-2 text-sm font-black text-[#7e3f28]"
                  >
                    <span className="truncate">{geography.cityName}</span>
                    <span className="h-2 w-2 rounded-full bg-[#b26b45]" aria-hidden="true" />
                  </Link>
                )}
              </div>
            </section>
          )}

          <div className="mt-auto space-y-3 pt-6">
            <div className="rounded-2xl border border-[#173f31]/10 bg-[#173f31]/[0.045] p-3">
              <div className="flex items-start gap-2">
                <ShieldCheck size={18} weight="fill" className="mt-0.5 shrink-0 text-[#356c55]" />
                <div>
                  <p className="text-xs font-black text-[#173f31]">{t('shell.sourceFirst')}</p>
                  <p className="mt-1 text-[11px] leading-4 text-[#667168]">{t('shell.sourceFirstBody')}</p>
                </div>
              </div>
            </div>
            <label className="sr-only" htmlFor="workspace-language">
              {t('language.label')}
            </label>
            <select
              id="workspace-language"
              value={locale}
              onChange={(event) => setLocale(event.target.value as Locale)}
              className="min-h-11 w-full rounded-xl border border-[#173f31]/12 bg-white/65 px-3 text-xs font-bold text-[#334139] outline-none focus:border-[#b26b45] focus:ring-4 focus:ring-[#b26b45]/15"
            >
              {LOCALES.map((option) => (
                <option key={option} value={option}>
                  {t(
                    `language.${option === 'en' ? 'english' : option === 'es' ? 'spanish' : option === 'vi' ? 'vietnamese' : 'chinese'}`,
                  )}
                </option>
              ))}
            </select>
            {userProfile ? (
              <Link
                to="/account"
                className="flex min-h-11 items-center gap-3 rounded-xl border border-[#173f31]/12 bg-white/65 px-3 text-sm font-bold text-[#26362e] hover:bg-white"
              >
                <UserCircle size={20} weight="duotone" />
                <span className="truncate">
                  {userProfile.displayName || userProfile.email || t('home.accountPortal')}
                </span>
              </Link>
            ) : (
              <LoginButton className="min-h-11 w-full rounded-xl bg-[#173f31] text-[#fff8e7] hover:bg-[#225742]" />
            )}
          </div>
        </aside>

        <div className="relative min-w-0 bg-[#f7f4eb]/58 shadow-[0_0_70px_rgba(33,46,38,0.06)]">
          <header className="sticky top-0 z-40 flex min-h-[68px] items-center justify-between gap-3 border-b border-[#173f31]/10 bg-[#faf6ec]/88 px-4 backdrop-blur-2xl lg:hidden">
            <Link to="/" className="flex min-w-0 items-center gap-3">
              <span className="grid h-10 w-10 shrink-0 place-items-center rounded-2xl bg-[#173f31] text-[#fff8e7] shadow-[0_10px_24px_rgba(23,63,49,0.22)]">
                <HandHeart size={21} weight="fill" />
              </span>
              <span className="min-w-0">
                <span className="block truncate text-[10px] font-black uppercase tracking-[0.19em] text-[#8b5b42]">
                  Planet Earth Help
                </span>
                <span className="block truncate font-serif text-lg font-black leading-tight tracking-[-0.035em] text-[#17231d]">
                  {routeLabel}
                </span>
              </span>
            </Link>
            <nav aria-label={t('shell.headerShortcuts')} className="flex shrink-0 items-center gap-1">
              <Link
                to="/#find-help"
                aria-label={t('shell.findByPlace')}
                title={t('shell.findByPlace')}
                className="hidden h-10 items-center gap-2 rounded-xl px-2.5 text-xs font-black text-[#425048] transition hover:bg-[#173f31]/7 hover:text-[#173f31] sm:inline-flex"
              >
                <MagnifyingGlass size={19} weight="bold" />
                <span className="hidden md:inline">{t('home.findSupport')}</span>
              </Link>
              <Link
                to="/#state-directory"
                aria-label={t('shell.townCenters')}
                title={t('shell.townCenters')}
                className="grid h-10 w-10 place-items-center rounded-xl text-[#425048] transition hover:bg-[#173f31]/7 hover:text-[#173f31]"
              >
                <MapTrifold size={21} weight="duotone" />
              </Link>
              <NotificationBell />
              {userProfile ? (
                <Link
                  to="/account"
                  aria-label={t('home.accountPortal')}
                  className="grid h-10 w-10 place-items-center rounded-xl text-[#173f31] hover:bg-[#173f31]/7"
                >
                  <UserCircle size={23} weight="duotone" />
                </Link>
              ) : (
                <LoginButton
                  variant="ghost"
                  className="h-10 min-w-10 rounded-xl border border-[#173f31]/10 bg-white/55 px-2 text-[#173f31] shadow-none hover:bg-white"
                />
              )}
            </nav>
          </header>

          <div className="peh-workspace-content min-w-0 pb-[calc(5.8rem+env(safe-area-inset-bottom))] lg:pb-0">
            {children}
          </div>
        </div>

        <aside
          aria-label="Help and page context"
          className="sticky top-0 z-20 hidden h-dvh overflow-y-auto border-l border-[#173f31]/10 bg-[#f8f4e9]/82 p-5 backdrop-blur-2xl 2xl:block"
        >
          <div className="mb-6">
            <p className="text-[10px] font-black uppercase tracking-[0.22em] text-[#8b5b42]">
              {t('shell.contextDesk')}
            </p>
            <h2 className="mt-2 font-serif text-2xl font-black leading-none tracking-[-0.045em] text-[#17231d]">
              {routeLabel}
            </h2>
            <p className="mt-2 text-sm leading-5 text-[#687269]">{t('shell.contextBody')}</p>
          </div>

          <section className="overflow-hidden rounded-[1.55rem] border border-[#173f31]/10 bg-[#173f31] text-[#fff8e7] shadow-[0_20px_48px_rgba(23,63,49,0.2)]">
            <div className="border-b border-white/12 p-5">
              <div className="flex items-center gap-2 text-[10px] font-black uppercase tracking-[0.2em] text-[#f4c88c]">
                <PhoneCall size={15} weight="fill" />
                {t('shell.helpNow')}
              </div>
              <p className="mt-3 font-serif text-2xl font-black leading-[1.05] tracking-[-0.04em]">
                {t('shell.helpNowBody')}
              </p>
            </div>
            <div className="grid grid-cols-2 gap-px bg-white/12">
              <a href="tel:211" className="min-h-20 bg-[#173f31] p-4 transition-colors hover:bg-[#225742]">
                <span className="block text-2xl font-black">211</span>
                <span className="mt-1 block text-[10px] font-bold uppercase tracking-wider text-white/65">
                  {t('shell.localServices')}
                </span>
              </a>
              <a href="tel:18663486479" className="min-h-20 bg-[#173f31] p-4 transition-colors hover:bg-[#225742]">
                <span className="block text-sm font-black">1-866-3-HUNGRY</span>
                <span className="mt-2 block text-[10px] font-bold uppercase tracking-wider text-white/65">
                  {t('shell.foodHotline')}
                </span>
              </a>
            </div>
          </section>

          <section className="mt-5 rounded-[1.45rem] border border-[#173f31]/10 bg-white/62 p-4 shadow-[0_14px_34px_rgba(49,57,49,0.07)]">
            <div className="flex items-center justify-between gap-3">
              <p className="text-xs font-black uppercase tracking-[0.15em] text-[#6f786f]">{t('shell.quickRoutes')}</p>
              <GlobeHemisphereWest size={18} weight="duotone" className="text-[#48735f]" />
            </div>
            <div className="mt-3 space-y-1">
              <Link
                to="/#find-help"
                className="flex min-h-11 items-center justify-between rounded-xl px-2 text-sm font-bold text-[#26362e] hover:bg-[#173f31]/7"
              >
                {t('shell.findByPlace')} <CaretRight size={14} />
              </Link>
              <Link
                to="/food"
                className="flex min-h-11 items-center justify-between rounded-xl px-2 text-sm font-bold text-[#26362e] hover:bg-[#173f31]/7"
              >
                {t('home.foodFinder')} <CaretRight size={14} />
              </Link>
              <Link
                to="/connecting-hands"
                className="flex min-h-11 items-center justify-between rounded-xl px-2 text-sm font-bold text-[#26362e] hover:bg-[#173f31]/7"
              >
                {t('home.howToHelp')} <CaretRight size={14} />
              </Link>
            </div>
          </section>

          <section className="mt-5 rounded-[1.45rem] border border-[#b26b45]/18 bg-[#fffaf0]/74 p-4">
            <div className="flex items-start gap-3">
              <CheckCircle size={20} weight="fill" className="mt-0.5 shrink-0 text-[#397257]" />
              <div>
                <h3 className="text-sm font-black text-[#26362e]">{t('shell.trustTitle')}</h3>
                <p className="mt-1 text-xs leading-5 text-[#687269]">{t('shell.trustBody')}</p>
                <Link
                  to="/about"
                  className="mt-3 inline-flex min-h-8 items-center gap-1 text-xs font-black text-[#8a452d] hover:underline"
                >
                  {t('nav.about')} <ArrowSquareOut size={13} weight="bold" />
                </Link>
              </div>
            </div>
          </section>

          <p className="mt-5 px-2 text-[11px] leading-5 text-[#747c75]">{t('home.emergencyNote')}</p>
        </aside>
      </div>

      <nav
        aria-label={t('nav.mobileNavigation')}
        className="fixed inset-x-2 bottom-2 z-50 flex items-stretch gap-0.5 rounded-[1.55rem] border border-[#173f31]/12 bg-[#fffaf0]/94 p-1.5 pb-[calc(0.375rem+env(safe-area-inset-bottom))] shadow-[0_20px_60px_rgba(28,42,33,0.22)] backdrop-blur-2xl lg:hidden"
      >
        {mobileNavigation.map((item) => (
          <MobileTab key={item.href} item={item} pathname={location.pathname} />
        ))}
        <Sheet>
          <SheetTrigger asChild>
            <button
              type="button"
              className="flex min-h-[62px] min-w-0 flex-1 flex-col items-center justify-center gap-1 rounded-2xl px-1 text-[10px] font-black text-[#657168] transition-colors hover:bg-[#173f31]/6 hover:text-[#173f31]"
            >
              <DotsThreeOutline size={22} weight="fill" />
              <span>{t('a11y.more')}</span>
            </button>
          </SheetTrigger>
          <SheetContent
            side="bottom"
            className="max-h-[88dvh] overflow-y-auto rounded-t-[2rem] border-[#173f31]/12 bg-[#faf6ec] px-5 pb-[calc(2rem+env(safe-area-inset-bottom))]"
          >
            <SheetHeader className="text-left">
              <SheetTitle className="font-serif text-3xl font-black tracking-[-0.045em] text-[#17231d]">
                {t('shell.moreTitle')}
              </SheetTitle>
              <SheetDescription>{t('shell.moreBody')}</SheetDescription>
            </SheetHeader>
            <div className="mt-6 grid grid-cols-2 gap-3">
              {[
                { href: '/connecting-hands', label: t('home.howToHelp'), icon: HandHeart },
                { href: '/account', label: t('home.accountPortal'), icon: UserCircle },
                { href: '/about', label: t('nav.about'), icon: ShieldCheck },
                { href: '/contact', label: t('nav.contact'), icon: PhoneCall },
              ].map((item) => {
                const IconComponent = item.icon;
                return (
                  <Link
                    key={item.href}
                    to={item.href}
                    className="flex min-h-24 flex-col justify-between rounded-2xl border border-[#173f31]/10 bg-white/72 p-4 text-sm font-black text-[#26362e] shadow-sm"
                  >
                    <IconComponent size={24} weight="duotone" className="text-[#47705d]" />
                    {item.label}
                  </Link>
                );
              })}
            </div>
            <div className="mt-5 rounded-2xl border border-[#173f31]/10 bg-white/62 p-4">
              <label
                className="mb-2 block text-xs font-black uppercase tracking-[0.16em] text-[#6f786f]"
                htmlFor="mobile-workspace-language"
              >
                {t('language.label')}
              </label>
              <select
                id="mobile-workspace-language"
                value={locale}
                onChange={(event) => setLocale(event.target.value as Locale)}
                className="min-h-12 w-full rounded-xl border border-[#173f31]/12 bg-white px-3 text-sm font-bold"
              >
                {LOCALES.map((option) => (
                  <option key={option} value={option}>
                    {t(
                      `language.${option === 'en' ? 'english' : option === 'es' ? 'spanish' : option === 'vi' ? 'vietnamese' : 'chinese'}`,
                    )}
                  </option>
                ))}
              </select>
            </div>
            {!userProfile && <LoginButton className="mt-4 min-h-12 w-full rounded-xl bg-[#173f31] text-[#fff8e7]" />}
          </SheetContent>
        </Sheet>
      </nav>
    </div>
  );
};
